import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Input,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  Icon,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Linkedin,
  Github,
  Twitter,
} from "lucide-react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";

const MotionBox = motion(Box);

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  // Dark mode color values
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const cardBg = useColorModeValue("gray.50", "gray.700");
  const formBg = useColorModeValue("gray.50", "gray.700");
  const inputBg = useColorModeValue("white", "gray.600");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("brand.50", "brand.900");
  const socialBg = useColorModeValue("gray.100", "gray.600");
  const socialHoverBg = useColorModeValue("brand.500", "brand.400");

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // EmailJS configuration - Replace with your actual IDs
  const SERVICE_ID = "service_bueo3bx";
  const TEMPLATE_ID = "template_cs4ibop";
  const PUBLIC_KEY = "BEsYg0A2iGYsqcdyd";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Send email using EmailJS
      const result = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          to_email: "osuolaleabdullahi@gmail.com",
          subject: formData.subject,
          message: formData.message,
          reply_to: formData.email,
        },
        PUBLIC_KEY
      );

      console.log("Email sent successfully:", result.text);

      toast({
        title: "Message sent!",
        description: "Thank you for your message. I'll get back to you soon!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("EmailJS error:", error);

      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: "osuolaleabdullahi@gmail.com",
      href: "mailto:osuolaleabdullahi@gmail.com",
    },
    {
      icon: Phone,
      title: "Phone",
      value: "+234 905 057 8612",
      href: "tel:+2349050578612",
    },
    {
      icon: MapPin,
      title: "Location",
      value: "Ibadan, Oyo State, Nigeria",
      href: "#",
    },
  ];

  const socialLinks = [
    {
      icon: Linkedin,
      href: "https://www.linkedin.com/in/jelili-abdullahi/",
      label: "LinkedIn",
    },
    {
      icon: Github,
      href: "https://github.com/AbdullahiAA",
      label: "GitHub",
    },
    {
      icon: Twitter,
      href: "https://x.com/Don_Triple_a",
      label: "Twitter",
    },
  ];

  return (
    <Box id="contact" py={20} bg={bgColor}>
      <Container maxW="7xl">
        <MotionBox
          textAlign="center"
          mb={16}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Heading
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="bold"
            color={textColor}
            mb={4}
          >
            Let's Work Together
          </Heading>
          <Text fontSize="xl" color={mutedTextColor} maxW="3xl" mx="auto">
            I'm always interested in new opportunities and exciting projects.
            Feel free to reach out if you'd like to collaborate or just say
            hello!
          </Text>
        </MotionBox>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12}>
          <MotionBox
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <VStack align="start" spacing={8}>
              <Box>
                <Heading size="lg" mb={4} color={textColor}>
                  Get in Touch
                </Heading>
                <Text color={mutedTextColor} mb={6}>
                  Whether you have a project in mind, a question about my work,
                  or just want to connect, I'd love to hear from you.
                </Text>
              </Box>

              <VStack align="start" spacing={4} w="full">
                {contactInfo.map((info, index) => (
                  <MotionBox
                    key={info.title}
                    as="a"
                    href={info.href}
                    w="full"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <HStack
                      p={4}
                      bg={cardBg}
                      borderRadius="lg"
                      _hover={{
                        bg: hoverBg,
                        transform: "translateX(4px)",
                      }}
                      transition="all 0.2s ease"
                    >
                      <Icon as={info.icon} w={5} h={5} color="brand.500" />
                      <VStack align="start" spacing={0}>
                        <Text
                          fontWeight="semibold"
                          color={textColor}
                          fontSize="sm"
                        >
                          {info.title}
                        </Text>
                        <Text color={mutedTextColor} fontSize="sm">
                          {info.value}
                        </Text>
                      </VStack>
                    </HStack>
                  </MotionBox>
                ))}
              </VStack>

              <Box>
                <Text fontWeight="semibold" color={textColor} mb={4}>
                  Follow Me
                </Text>
                <HStack spacing={4}>
                  {socialLinks.map((social, index) => (
                    <MotionBox
                      key={social.label}
                      as="a"
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      p={3}
                      bg={socialBg}
                      borderRadius="lg"
                      _hover={{
                        bg: socialHoverBg,
                        color: "white",
                        transform: "translateY(-2px)",
                      }}
                      transition="all 0.2s ease"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      lineHeight={0}
                    >
                      <Icon as={social.icon} boxSize={5} />
                    </MotionBox>
                  ))}
                </HStack>
              </Box>
            </VStack>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Box
              as="form"
              onSubmit={handleSubmit}
              bg={formBg}
              p={8}
              borderRadius="xl"
              border="1px"
              borderColor={borderColor}
            >
              <Heading size="lg" mb={6} color={textColor}>
                Send a Message
              </Heading>

              <VStack spacing={6}>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} w="full">
                  <FormControl isRequired>
                    <FormLabel color={mutedTextColor}>Name</FormLabel>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      bg={inputBg}
                      borderColor={borderColor}
                      color={textColor}
                      _focus={{
                        borderColor: "brand.500",
                        boxShadow: "0 0 0 1px #3182CE",
                      }}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel color={mutedTextColor}>Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      bg={inputBg}
                      borderColor={borderColor}
                      color={textColor}
                      _focus={{
                        borderColor: "brand.500",
                        boxShadow: "0 0 0 1px #3182CE",
                      }}
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl isRequired>
                  <FormLabel color={mutedTextColor}>Subject</FormLabel>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    bg={inputBg}
                    borderColor={borderColor}
                    color={textColor}
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px #3182CE",
                    }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color={mutedTextColor}>Message</FormLabel>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    bg={inputBg}
                    borderColor={borderColor}
                    color={textColor}
                    _focus={{
                      borderColor: "brand.500",
                      boxShadow: "0 0 0 1px #3182CE",
                    }}
                  />
                </FormControl>

                <Button
                  type="submit"
                  leftIcon={<Send size={16} />}
                  isLoading={isLoading}
                  loadingText="Sending..."
                  size="lg"
                  w="full"
                  variant="solid"
                >
                  Send Message
                </Button>
              </VStack>
            </Box>
          </MotionBox>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default Contact;
